import React, { useEffect, useCallback, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const MenuItemModal = ({ 
  showModal, 
  menuItem, 
  closeModal,
  menuItems,
  currentIndex,
  setCurrentIndex,
  selectedLanguage,
  setSelectedLanguage 
}) => {
  const [touchStart, setTouchStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const navigateMenuItem = useCallback((direction) => {
    if (!menuItems || menuItems.length === 0) return;
    
    if (direction === 'next') {
      setCurrentIndex((prev) => 
        prev === menuItems.length - 1 ? 0 : prev + 1
      );
    } else {
      setCurrentIndex((prev) => 
        prev === 0 ? menuItems.length - 1 : prev - 1
      );
    }
  }, [menuItems, setCurrentIndex]);

  // Touch/swipe handling for mobile
  const handleTouchStart = useCallback((e) => {
    if (!menuItems || menuItems.length <= 1) return;
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY, time: Date.now() });
    setIsDragging(true);
  }, [menuItems]);

  const handleTouchMove = useCallback((e) => {
    if (!menuItems || menuItems.length <= 1 || !touchStart || !isDragging) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    
    // Only track horizontal movement if it's primarily horizontal
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault(); // Prevent scrolling
      e.stopPropagation(); // Prevent event bubbling
      
      // Create elastic resistance effect like iPhone
      const maxDrag = window.innerWidth * 0.4;
      let dampedDelta;
      
      if (Math.abs(deltaX) <= maxDrag) {
        dampedDelta = deltaX;
      } else {
        // Apply exponential resistance beyond max drag
        const excess = Math.abs(deltaX) - maxDrag;
        const resistance = maxDrag + (excess * 0.2);
        dampedDelta = deltaX > 0 ? resistance : -resistance;
      }
      
      setDragOffset(dampedDelta);
    }
  }, [menuItems, touchStart, isDragging]);

  const handleTouchEnd = useCallback((e) => {
    if (!menuItems || menuItems.length <= 1 || !touchStart) return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaY = touch.clientY - touchStart.y;
    const deltaTime = Date.now() - touchStart.time;
    
    // Check if it's a valid swipe (horizontal, fast enough, long enough)
    if (Math.abs(deltaX) > Math.abs(deltaY) && // More horizontal than vertical
        (Math.abs(deltaX) > 100 || (Math.abs(deltaX) > 50 && deltaTime < 300))) { // Distance or speed threshold
      
      if (deltaX > 0) {
        navigateMenuItem('prev'); // Swipe right = previous
      } else {
        navigateMenuItem('next'); // Swipe left = next
      }
    }
    
    // Reset states
    setTouchStart(null);
    setDragOffset(0);
    setIsDragging(false);
  }, [menuItems, touchStart, navigateMenuItem]);

  const getDisplayText = (item, language) => {
    if (language === 'spanish') return item.description;
    return item.translations[language] || item.description;
  };

  const languages = [
    { code: 'spanish', flag: '🇪🇸', name: 'Español' },
    { code: 'french', flag: '🇫🇷', name: 'Français' },
    { code: 'dutch', flag: '🇳🇱', name: 'Nederlands' },
    { code: 'english', flag: '🇺🇸', name: 'English' },
    { code: 'german', flag: '🇩🇪', name: 'Deutsch' }
  ];

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showModal) return;
      
      if (e.key === 'Escape') {
        closeModal();
      } else if (e.key === 'ArrowRight') {
        navigateMenuItem('next');
      } else if (e.key === 'ArrowLeft') {
        navigateMenuItem('prev');
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [showModal, closeModal, menuItems, navigateMenuItem]);

  if (!showModal || !menuItem) return null;

  // Map menu items to existing food images
  const dishImages = {
    'Capuchino de Bogavante': '/images/lobster_thermidor.jpg',
    '½ Ensalada de Bogavante': '/images/lobster_salad_platter.jpg',
    '½ Bogavante con crema de verduras o mantequilla de ajo': '/images/lobster_thermidor.jpg',
    'Ensalada de bogavante "parisien"': '/images/lobster_salad_platter.jpg',
    'Scampis "diabolo" en crema picante': '/images/seafood_platter_1.jpg',
    'Tartar de Atun con aguacate': '/images/gourmet_appetizer_plating.jpg',
    'Foie gras de pato casero con mermelada de cebolla': '/images/appetizer_course_1.jpg',
    'Solomillo de ternera con foie gras y reduccion de Pedro Ximenez': '/images/meat_dish_plating_1.jpg',
    'Steak Nou Valentino (queso de cabra y mermelada de pimientos)': '/images/steak_with_vegetables.jpg',
    'Steak "Atomiun" (pimienta negra, nata, flambeado al Brandy)': '/images/meat_dish_plating_2.jpg',
    'Steak con salsa Gorgonzola': '/images/main_course_specialty.jpg',
    'Escalope de foie gras salteado con manzana y salsa porto': '/images/appetizer_course_2.jpg',
    'Vieiras gratinadas': '/images/seafood_platter_2.jpg',
    'Caracoles en Hojaldre (Salsa de tomate, ajo y nata)': '/images/snails_garlic_herbs.jpg',
    'Croquetas de gambitas grises': '/images/appetizer_course_3.jpg',
    'Croquetas de queso': '/images/appetizer_course_4.jpg',
    'Carpaccio de ternera': '/images/fine_dining_presentation.jpg'
  };

  // Fallback image for dishes without specific images
  const fallbackImage = '/images/fine_dining_presentation.jpg';
  const dishImage = dishImages[menuItem.name] || fallbackImage;

  return (
    <div 
      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close button */}
      <button
        onClick={closeModal}
        className="absolute top-4 right-4 md:top-6 md:right-6 z-50 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 md:p-3 text-white transition-colors duration-200"
        aria-label="Close modal"
      >
        <X size={20} className="md:w-6 md:h-6" />
      </button>

      {/* Navigation buttons */}
      {menuItems && menuItems.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateMenuItem('prev');
            }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-50 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 md:p-3 text-white transition-colors duration-200"
          >
            <ChevronLeft size={20} className="md:w-6 md:h-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateMenuItem('next');
            }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-50 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 md:p-3 text-white transition-colors duration-200"
          >
            <ChevronRight size={20} className="md:w-6 md:h-6" />
          </button>
        </>
      )}

      {/* Main modal container */}
      <div 
        className={`relative w-full max-w-5xl h-full max-h-[85vh] flex flex-col items-center justify-center ${
          isDragging ? 'transition-none' : 'transition-all duration-300 ease-out'
        }`}
        style={{
          transform: `translateX(${dragOffset}px)`,
          opacity: Math.max(0.3, 1 - Math.abs(dragOffset) / (window.innerWidth * 0.6)),
          scale: Math.max(0.95, 1 - Math.abs(dragOffset) / (window.innerWidth * 2))
        }}
      >
        {/* Dish image - responsive container that maintains aspect ratio */}
        <div className="relative w-full max-w-4xl mb-6 rounded-lg overflow-hidden">
          <img
            src={dishImage}
            alt={menuItem.name}
            className="w-full h-auto max-h-[60vh] object-contain rounded-lg"
            onError={(e) => {
              e.target.src = fallbackImage;
            }}
          />
          {/* Elegant overlay for better text readability - only at bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/60 to-transparent"></div>
          
          {/* Price positioned over the image */}
          <div className="absolute top-6 right-6">
            <div className="bg-red-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-lg font-light tracking-wider shadow-lg">
              {menuItem.price}
            </div>
          </div>
        </div>
        
        {/* Dish information - non-invasive at bottom */}
        <div className="text-center text-white max-w-3xl px-4 md:px-6">
          <h3 className="text-xl md:text-2xl lg:text-3xl font-light tracking-wide text-white mb-4 leading-tight">
            {menuItem.name}
          </h3>
          
          <div className="w-16 h-px bg-red-600 mx-auto mb-6"></div>
          
          <div className="text-base md:text-lg text-gray-300 font-light leading-relaxed mb-6">
            {getDisplayText(menuItem, selectedLanguage).split('\n').map((line, lineIndex) => (
              <div key={lineIndex} className={`${line.startsWith('•') || line.includes('courses included') ? 'text-center' : ''} ${line.trim() === '' ? 'h-4' : ''}`}>
                {line || '\u00A0'}
              </div>
            ))}
          </div>

          {/* Menu navigation indicator */}
          {menuItems && menuItems.length > 1 && (
            <div className="flex items-center justify-center gap-4 mb-6">
              <span className="text-xs text-white/50">
                {currentIndex + 1} / {menuItems.length}
              </span>
            </div>
          )}
          
          {/* Language flags */}
          <div className="flex justify-center gap-4 mb-4">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => setSelectedLanguage(lang.code)}
                className={`text-2xl md:text-3xl hover:scale-125 transition-all duration-200 p-2 rounded-lg ${
                  selectedLanguage === lang.code 
                    ? 'opacity-100 scale-125 bg-red-600/20 border border-red-600/50' 
                    : 'opacity-60 hover:opacity-100 hover:bg-white/10'
                }`}
                title={lang.name}
              >
                {lang.flag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Click outside to close */}
      <div
        className="absolute inset-0 -z-10"
        onClick={closeModal}
      />
    </div>
  );
};

export default MenuItemModal;